import { useLazyQuery, useMutation } from '@apollo/client';
import { useCallback, useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import { eluveAdminHasuraContext } from '@eluve/apollo-client';
import { useToast } from '@eluve/components';

import { setTenantTemplateBundleMutation } from '../tenant/$tenantId/components/TenantTemplateBundleToggle';

import type {
  AffectedTenant,
  AffectedUser,
  ReplacementBundleOption,
} from './DisableBundleConfirmationDialog';
import {
  bulkUpsertTenantTemplateBundlesMutation,
  clearDefaultTemplateBundleIdMutation,
  clearTemplateBundleDefaultsMutation,
  deactivateTemplateBundleMutation,
  disableTenantTemplateBundlesMutation,
  getAffectedTenantsForBundleDisableQuery,
  getAffectedUsersForBundleDisableQuery,
  getAffectedUsersForTenantBundleDisableQuery,
  getCollidingTemplateBundleDefaultsQuery,
  getGlobalDefaultBundleQuery,
  getReplacementBundleOptionsForTenantQuery,
  getReplacementBundleOptionsQuery,
  getTemplateBundleDisplayName,
  getTenantBundleDisableSafetyQuery,
  migrateDefaultTemplateBundleIdMutation,
  migrateTemplateBundleDefaultsMutation,
  updateTemplateBundleIsDefaultForNewTenantsMutation,
} from './template-bundle.operations';

export interface RequestDisableOptions {
  /**
   * Whether the bundle being disabled is currently the default for new tenants.
   * When true and a replacement is chosen (global mode only), the replacement
   * inherits the flag. `deactivateTemplateBundleMutation` clears the flag on
   * the old bundle, so without this transfer the system would be left with
   * no default-for-new-tenants bundle.
   */
  wasDefaultForNewTenants?: boolean;
}

type UseDisableBundleFlowOptions =
  | { mode: 'global' }
  | { mode: 'tenant'; tenantId: string };

export function useDisableBundleFlow(options: UseDisableBundleFlowOptions) {
  const toast = useToast();
  const apiClient = useApiClient();

  // Mutations
  const [deactivateBundle] = useMutation(deactivateTemplateBundleMutation, {
    context: eluveAdminHasuraContext,
  });

  const [clearDefaults] = useMutation(clearDefaultTemplateBundleIdMutation, {
    context: eluveAdminHasuraContext,
  });

  const [migrateDefaults] = useMutation(
    migrateDefaultTemplateBundleIdMutation,
    { context: eluveAdminHasuraContext },
  );

  const [clearBundleRules] = useMutation(clearTemplateBundleDefaultsMutation, {
    context: eluveAdminHasuraContext,
  });

  const [migrateBundleRules] = useMutation(
    migrateTemplateBundleDefaultsMutation,
    { context: eluveAdminHasuraContext },
  );

  const [disableAllTenantBundles] = useMutation(
    disableTenantTemplateBundlesMutation,
    { context: eluveAdminHasuraContext },
  );

  const [bulkUpsertTenantBundles] = useMutation(
    bulkUpsertTenantTemplateBundlesMutation,
    { context: eluveAdminHasuraContext },
  );

  const [setIsDefaultForNewTenants] = useMutation(
    updateTemplateBundleIsDefaultForNewTenantsMutation,
    { context: eluveAdminHasuraContext },
  );

  const [disableSingleTenantBundle] = useMutation(
    setTenantTemplateBundleMutation,
    {
      context: eluveAdminHasuraContext,
      refetchQueries: ['getTenantTemplateBundles'],
    },
  );

  // Lazy queries
  const [fetchAffectedTenants] = useLazyQuery(
    getAffectedTenantsForBundleDisableQuery,
    { context: eluveAdminHasuraContext, fetchPolicy: 'network-only' },
  );
  const [fetchGlobalAffectedUsers] = useLazyQuery(
    getAffectedUsersForBundleDisableQuery,
    { context: eluveAdminHasuraContext, fetchPolicy: 'network-only' },
  );
  const [fetchTenantAffectedUsers] = useLazyQuery(
    getAffectedUsersForTenantBundleDisableQuery,
    { context: eluveAdminHasuraContext, fetchPolicy: 'network-only' },
  );
  const [fetchGlobalReplacementOptions] = useLazyQuery(
    getReplacementBundleOptionsQuery,
    { context: eluveAdminHasuraContext, fetchPolicy: 'network-only' },
  );
  const [fetchTenantReplacementOptions] = useLazyQuery(
    getReplacementBundleOptionsForTenantQuery,
    { context: eluveAdminHasuraContext, fetchPolicy: 'network-only' },
  );
  const [fetchTenantBundleDisableSafety] = useLazyQuery(
    getTenantBundleDisableSafetyQuery,
    { context: eluveAdminHasuraContext, fetchPolicy: 'network-only' },
  );
  const [fetchCollidingRules] = useLazyQuery(
    getCollidingTemplateBundleDefaultsQuery,
    { context: eluveAdminHasuraContext, fetchPolicy: 'network-only' },
  );
  const [fetchGlobalDefault] = useLazyQuery(getGlobalDefaultBundleQuery, {
    context: eluveAdminHasuraContext,
    fetchPolicy: 'network-only',
  });

  // State
  const [pendingBundleId, setPendingBundleId] = useState<string | null>(null);
  const [pendingBundleName, setPendingBundleName] = useState('');
  const [affectedTenants, setAffectedTenants] = useState<AffectedTenant[]>([]);
  const [affectedUsers, setAffectedUsers] = useState<AffectedUser[]>([]);
  const [replacementOptions, setReplacementOptions] = useState<
    ReplacementBundleOption[]
  >([]);
  const [replacementBundleId, setReplacementBundleId] = useState<string | null>(
    null,
  );
  const [pendingWasDefaultForNewTenants, setPendingWasDefaultForNewTenants] =
    useState(false);
  const [isLoadingAffected, setIsLoadingAffected] = useState(false);
  const [isDisabling, setIsDisabling] = useState(false);
  const [pendingAppointmentCount, setPendingAppointmentCount] = useState(0);

  const resetState = useCallback(() => {
    setPendingBundleId(null);
    setReplacementBundleId(null);
    setPendingWasDefaultForNewTenants(false);
    setPendingAppointmentCount(0);
  }, []);

  const requestDisable = useCallback(
    async (
      bundleId: string,
      bundleName: string,
      requestOptions?: RequestDisableOptions,
    ) => {
      // Safety net: when disabling a tenant's last enabled bundle, the
      // resolver would start throwing for every appointment (bundles are
      // enabled for all tenants, with no legacy fallback under the
      // post-ENG-4256 design). Block the disable here before the confirm
      // dialog opens, with an explanatory toast that points at the two
      // remediation paths.
      if (options.mode === 'tenant') {
        try {
          const { data: safety } = await fetchTenantBundleDisableSafety({
            variables: { tenantId: options.tenantId, bundleId },
          });
          const otherEnabled = safety?.otherEnabled?.aggregate?.count ?? 0;
          if (otherEnabled === 0) {
            toast.error(
              'Cannot disable the last enabled bundle. Enable another bundle first.',
            );
            return;
          }
        } catch {
          toast.error(
            'Failed to verify whether this is the tenant’s last enabled bundle.',
          );
          return;
        }
      }

      setPendingBundleId(bundleId);
      setPendingBundleName(bundleName);
      setPendingWasDefaultForNewTenants(
        requestOptions?.wasDefaultForNewTenants ?? false,
      );
      setAffectedTenants([]);
      setAffectedUsers([]);
      setReplacementOptions([]);
      setReplacementBundleId(null);
      setPendingAppointmentCount(0);
      setIsLoadingAffected(true);

      // Advisory count for the confirm dialog — never block the dialog on it.
      const fetchPendingCount = async (): Promise<number> => {
        try {
          const res =
            await apiClient.adminLlm.countPendingAppointmentsForBundle({
              params: { bundleId },
              body:
                options.mode === 'tenant' ? { tenantId: options.tenantId } : {},
            });
          return res.status === 200 ? res.body.count : 0;
        } catch {
          return 0;
        }
      };

      try {
        if (options.mode === 'global') {
          // Pre-check: the global default bundle can't be deactivated while it
          // holds the role (a DB trigger is the backstop). Refuse early with a
          // friendly message so the admin reassigns the global default first.
          const globalDefaultResult = await fetchGlobalDefault();
          const isGlobalDefault =
            globalDefaultResult.data?.templateBundleDefaults?.[0]
              ?.templateBundleId === bundleId;
          if (isGlobalDefault) {
            toast.error(
              'This is the global default bundle — pick a new global default in the dropdown first, then disable this one.',
            );
            resetState();
            return;
          }

          const [tenantsResult, usersResult, replacementsResult, pendingCount] =
            await Promise.all([
              fetchAffectedTenants({ variables: { bundleId } }),
              fetchGlobalAffectedUsers({ variables: { bundleId } }),
              fetchGlobalReplacementOptions({ variables: { bundleId } }),
              fetchPendingCount(),
            ]);

          setPendingAppointmentCount(pendingCount);
          setAffectedTenants(
            (tenantsResult.data?.tenantTemplateBundles ?? []).map((ttb) => ({
              tenantId: ttb.tenantId,
              name: ttb.tenant?.name ?? 'Unknown',
              otherBundleCount:
                ttb.tenant?.tenantTemplateBundlesAggregate?.aggregate?.count ??
                0,
            })),
          );
          setAffectedUsers(
            (usersResult.data?.tenantUsers ?? []).map((tu) => ({
              userId: tu.userId,
              tenantId: tu.tenantId,
              firstName: tu.user?.firstName ?? '',
              lastName: tu.user?.lastName ?? '',
              email: tu.user?.email ?? '',
              tenantName: tu.tenant?.name ?? '',
            })),
          );
          setReplacementOptions(
            (replacementsResult.data?.llmModelPromptTemplates ?? []).map(
              (b) => ({
                id: b.id,
                displayName: getTemplateBundleDisplayName(b),
              }),
            ),
          );
        } else {
          const [usersResult, replacementsResult, pendingCount] =
            await Promise.all([
              fetchTenantAffectedUsers({
                variables: { bundleId, tenantId: options.tenantId },
              }),
              fetchTenantReplacementOptions({
                variables: { bundleId, tenantId: options.tenantId },
              }),
              fetchPendingCount(),
            ]);
          setPendingAppointmentCount(pendingCount);
          setAffectedUsers(
            (usersResult.data?.tenantUsers ?? []).map((tu) => ({
              userId: tu.userId,
              tenantId: tu.tenantId,
              firstName: tu.user?.firstName ?? '',
              lastName: tu.user?.lastName ?? '',
              email: tu.user?.email ?? '',
            })),
          );
          setReplacementOptions(
            (replacementsResult.data?.llmModelPromptTemplates ?? []).map(
              (b) => ({
                id: b.id,
                displayName: getTemplateBundleDisplayName(b),
              }),
            ),
          );
        }
      } catch {
        toast.error('Failed to load affected tenants and users');
        // Fully reset so the next requestDisable doesn't briefly render stale
        // replacement state (replacementBundleId / pendingWasDefaultForNewTenants)
        // from this aborted attempt.
        resetState();
      } finally {
        setIsLoadingAffected(false);
      }
    },
    [
      options,
      apiClient,
      fetchAffectedTenants,
      fetchGlobalAffectedUsers,
      fetchTenantAffectedUsers,
      fetchGlobalReplacementOptions,
      fetchTenantReplacementOptions,
      fetchTenantBundleDisableSafety,
      fetchGlobalDefault,
      resetState,
      toast,
    ],
  );

  const userDefaultsWhere = useCallback(
    (bundleId: string) =>
      options.mode === 'global'
        ? { defaultTemplateBundleId: { _eq: bundleId } }
        : {
            defaultTemplateBundleId: { _eq: bundleId },
            tenantId: { _eq: options.tenantId },
          },
    [options],
  );

  const bundleRulesWhere = useCallback(
    (bundleId: string) =>
      options.mode === 'global'
        ? { templateBundleId: { _eq: bundleId } }
        : {
            templateBundleId: { _eq: bundleId },
            tenantId: { _eq: options.tenantId },
          },
    [options],
  );

  const handleConfirmDisable = useCallback(async () => {
    if (!pendingBundleId) return;
    const oldBundleId = pendingBundleId;
    const newBundleId = replacementBundleId;
    setIsDisabling(true);

    const disableOldBundleTenantsIfNeeded = async () => {
      if (options.mode !== 'global' || affectedTenants.length === 0) return;
      try {
        await disableAllTenantBundles({
          variables: { bundleId: oldBundleId },
        });
      } catch {
        toast.error(
          'Bundle disabled but failed to disable tenant associations. Please disable them manually.',
        );
      }
    };

    // Returns true when the replacement is enabled for every affected tenant,
    // false when the bulk-upsert failed. Returns true (no-op) when there's
    // nothing to do. Callers use this to decide whether migrating user
    // defaults to the replacement is safe.
    const enableReplacementForTenantsIfNeeded = async (): Promise<boolean> => {
      if (
        !newBundleId ||
        options.mode !== 'global' ||
        affectedTenants.length === 0
      ) {
        return true;
      }
      try {
        await bulkUpsertTenantBundles({
          variables: {
            objects: affectedTenants.map((t) => ({
              tenantId: t.tenantId,
              llmModelPromptTemplateId: newBundleId,
              isEnabled: true,
            })),
          },
        });
        return true;
      } catch {
        toast.error(
          'Bundle disabled but failed to enable the replacement for some tenants. Please verify manually.',
        );
        return false;
      }
    };

    const transferDefaultForNewTenantsIfNeeded = async () => {
      if (
        !newBundleId ||
        options.mode !== 'global' ||
        !pendingWasDefaultForNewTenants
      ) {
        return;
      }
      try {
        await setIsDefaultForNewTenants({
          variables: { id: newBundleId, isDefaultForNewTenants: true },
        });
      } catch {
        toast.error(
          'Bundle disabled but failed to transfer the default-for-new-tenants flag. Please set it manually on the replacement.',
        );
      }
    };

    const updateBundleRulesIfNeeded = async (skip: boolean) => {
      if (skip) {
        // Same skip-gate as user defaults: don't repoint admin rule rows at a
        // bundle the replacement-enable step failed for.
        toast.error(
          'Admin rule defaults were not migrated because the replacement could not be enabled. Please update them manually.',
        );
        return;
      }
      try {
        if (!newBundleId) {
          await clearBundleRules({
            variables: { where: bundleRulesWhere(oldBundleId) },
          });
          return;
        }
        // See getCollidingTemplateBundleDefaultsQuery for why we pre-delete
        // colliders before the bulk migrate.
        const { data } = await fetchCollidingRules({
          variables: {
            oldBundleScope: bundleRulesWhere(oldBundleId),
            newBundleScope: bundleRulesWhere(newBundleId),
          },
        });
        const oldRows = data?.oldRows ?? [];
        if (oldRows.length === 0) return;
        const newRows = data?.newRows ?? [];
        // specialty is citext in Postgres, so the unique constraint
        // treats 'Cardiology' and 'cardiology' as the same slot. Lowercase
        // it in the key so JS collision detection matches the DB.
        const keyOf = (r: {
          tenantId: string | null;
          specialty?: string | null;
          language?: string | null;
        }) =>
          `${r.tenantId ?? ''}\x00${r.specialty?.toLowerCase() ?? ''}\x00${r.language ?? ''}`;
        const newKeys = new Set(newRows.map(keyOf));
        const collidingIds = oldRows
          .filter((r) => newKeys.has(keyOf(r)))
          .map((r) => r.id);
        if (collidingIds.length > 0) {
          await clearBundleRules({
            variables: { where: { id: { _in: collidingIds } } },
          });
        }
        await migrateBundleRules({
          variables: {
            where: bundleRulesWhere(oldBundleId),
            newBundleId,
          },
        });
      } catch {
        toast.error(
          newBundleId
            ? 'Bundle disabled but failed to migrate admin rule defaults. Please update them manually.'
            : 'Bundle disabled but failed to clear admin rule defaults. Please clear them manually.',
        );
      }
    };

    const updateUserDefaultsIfNeeded = async (skip: boolean) => {
      if (affectedUsers.length === 0) return;
      if (skip) {
        // The replacement could not be enabled for this tenant, so migrating
        // user defaults to it would leave them pointing at a disabled bundle.
        toast.error(
          'User defaults were not migrated because the replacement could not be enabled. Please update them manually.',
        );
        return;
      }
      try {
        if (newBundleId) {
          await migrateDefaults({
            variables: {
              where: userDefaultsWhere(oldBundleId),
              newBundleId,
            },
          });
        } else {
          await clearDefaults({
            variables: { where: userDefaultsWhere(oldBundleId) },
          });
        }
      } catch {
        toast.error(
          newBundleId
            ? 'Bundle disabled but failed to migrate user defaults. Please update them manually.'
            : 'Bundle disabled but failed to clear user defaults. Please clear them manually.',
        );
      }
    };

    // Same skip gate as the user-default migration: don't repoint at a bundle
    // the replacement-enable step failed for.
    const repointPendingAppointmentsIfNeeded = async (skip: boolean) => {
      if (!newBundleId) return;
      if (skip) {
        if (pendingAppointmentCount > 0) {
          toast.error(
            'Pending appointments were not repointed because the replacement could not be enabled. Please update them manually.',
          );
        }
        return;
      }
      try {
        const response =
          await apiClient.adminLlm.repointPendingAppointmentsToReplacementBundle(
            {
              body: {
                disabledBundleId: oldBundleId,
                replacementBundleId: newBundleId,
                ...(options.mode === 'tenant'
                  ? { tenantId: options.tenantId }
                  : {}),
              },
            },
          );
        if (response.status === 200) return;
      } catch {
        // ts-rest resolves error envelopes; both a non-200 and a thrown error
        // mean the repoint didn't succeed — fall through to the shared toast.
      }
      toast.error(
        'Bundle disabled but failed to repoint pending appointments. Please update them manually.',
      );
    };

    try {
      // Step 1: Deactivate (global) OR flip the single tenant's row (tenant).
      if (options.mode === 'global') {
        await deactivateBundle({
          variables: { id: oldBundleId },
        });
      } else {
        await disableSingleTenantBundle({
          variables: {
            tenantId: options.tenantId,
            llmModelPromptTemplateId: oldBundleId,
            isEnabled: false,
          },
        });
      }

      // Step 2: Enable the replacement on the affected tenant(s) BEFORE
      // touching user defaults, so any default we point at the replacement
      // lands on an actually-enabled bundle. In tenant mode this is a single
      // upsert; in global mode it's a bulk upsert for every affected tenant.
      // If the enable fails, we surface a toast and skip the user-default
      // migration to avoid leaving users pointing at a disabled bundle.
      let replacementEnabled = true;
      if (newBundleId && options.mode === 'tenant') {
        try {
          await disableSingleTenantBundle({
            variables: {
              tenantId: options.tenantId,
              llmModelPromptTemplateId: newBundleId,
              isEnabled: true,
            },
          });
        } catch {
          replacementEnabled = false;
          toast.error(
            'Bundle disabled but failed to enable the replacement for this tenant. Please verify manually.',
          );
        }
      } else if (newBundleId && options.mode === 'global') {
        replacementEnabled = await enableReplacementForTenantsIfNeeded();
      }

      // Step 3: Disjoint tenant-association, user-default, and bundle-level
      // writes run concurrently. Skip the user-default migration if step 2
      // failed — otherwise we'd point defaults at a disabled bundle.
      await Promise.all([
        disableOldBundleTenantsIfNeeded(),
        updateUserDefaultsIfNeeded(!replacementEnabled),
        updateBundleRulesIfNeeded(!replacementEnabled),
        transferDefaultForNewTenantsIfNeeded(),
        repointPendingAppointmentsIfNeeded(!replacementEnabled),
      ]);
    } catch {
      toast.error('Failed to disable the bundle');
    } finally {
      setIsDisabling(false);
      resetState();
    }
  }, [
    pendingBundleId,
    replacementBundleId,
    pendingWasDefaultForNewTenants,
    pendingAppointmentCount,
    options,
    apiClient,
    affectedTenants,
    affectedUsers,
    deactivateBundle,
    disableSingleTenantBundle,
    disableAllTenantBundles,
    bulkUpsertTenantBundles,
    setIsDefaultForNewTenants,
    clearDefaults,
    migrateDefaults,
    clearBundleRules,
    migrateBundleRules,
    fetchCollidingRules,
    userDefaultsWhere,
    bundleRulesWhere,
    resetState,
    toast,
  ]);

  const cancelDisable = useCallback(() => {
    resetState();
  }, [resetState]);

  return {
    requestDisable,
    dialogProps: {
      isOpen: pendingBundleId !== null,
      onOpenChange: (open: boolean) => {
        if (!open) resetState();
      },
      mode: options.mode,
      bundleName: pendingBundleName,
      affectedTenants,
      affectedUsers,
      replacementOptions,
      replacementBundleId,
      onReplacementBundleChange: setReplacementBundleId,
      pendingAppointmentCount,
      isLoading: isLoadingAffected,
      isDisabling,
      onConfirm: handleConfirmDisable,
      onCancel: cancelDisable,
    },
  } as const;
}
